import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import MaskedInput from 'react-text-mask';
import { colors, SovosTextField } from 's1-ui';
import AccountNumbersField from './AccountNumbersField';

const { blueDark, blueMedium } = colors;

const styles = {
  companyInfo: {
    color: blueDark,
    fontSize: 18,
    fontWeight: 300
  },
  container: {
    paddingTop: 20
  },
  group1: {
    paddingTop: 40,
    display: 'flex',
    width: '100%'
  },
  contactDetails: {
    paddingTop: 80,
    color: blueDark,
    fontSize: 18,
    fontWeight: 300
  },
  textField: {
    flexGrow: 1,
    paddingRight: '50px'
  },
  group2: {
    paddingTop: 40,
    display: 'flex',
    width: '100%'
  },
  cityStateZip: {
    width: 450
  },
  underline: {
    '&:not(.bullshit):not(.classes):not(.ftw):after': {
      borderBottom: `2px solid ${blueMedium}`
    },
    '&:hover:not(.just):not(.trying):not(.to):not(.win)': {
      '&:before, &:after': { borderBottom: `2px solid ${blueMedium}` },
    }
  },
  errors: {
    marginTop: 10,
    marginBottom: 10
  }
};

// eslint-disable-next-line arrow-body-style
const getOnChange = (parentValue, parentOnChange, key) => {
  return function onChange(newValue) {
    const setValue = newValue === '' ? undefined : newValue;
    parentOnChange({ ...parentValue, [key]: setValue });
  };
};

const TextField = ({
  value, onChange, error, underline, label, style, className, disabled
}) => {
  const hasError = error != null;
  return (
    <React.Fragment>
      <SovosTextField
        InputProps={ {
          placeholder: label,
          error: hasError,
          disabled
        } }
        className={ className }
        style={ style }
        error={ hasError }
        helperText={ error }
        label={ label }
        value={ value }
        classes={ { underline } }
        onChange={ e => onChange(e.target.value) }
      />
    </React.Fragment>
  );
};

function PhoneNumberMask(props) {
  const { inputRef, ...other } = props;
  return (
    <MaskedInput
      { ...other }
      ref={ ref => inputRef(ref ? ref.inputElement : null) }
      mask={ ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/] }
      placeholderChar="_"
      keepCharPositions
    />
  );
}

function StateMask(props) {
  const { inputRef, ...other } = props;
  return (
    <MaskedInput
      { ...other }
      ref={ ref => inputRef(ref ? ref.inputElement : null) }
      mask={ [/[a-zA-Z]/, /[a-zA-Z]/] }
      placeholderChar="_"
      keepCharPositions
    />
  );
}

function ZipCodeMask(props) {
  const { inputRef, ...other } = props;
  return (
    <MaskedInput
      { ...other }
      ref={ ref => inputRef(ref ? ref.inputElement : null) }
      mask={ [/\d/, /\d/, /\d/, /\d/, /\d/] }
      placeholderChar="_"
      keepCharPositions
    />
  );
}

const MaskedField = ({
  value, onChange, error, underline, style, label, mask
}) => {
  const hasError = error !== undefined;
  return (
    <React.Fragment>
      <SovosTextField
        InputProps={ {
          inputComponent: mask,
          error: hasError
        } }
        error={ hasError }
        style={ style }
        label={ label }
        value={ value }
        classes={ { underline } }
        onChange={ e => onChange(e.target.value) }
      />
    </React.Fragment>
  );
};

export const UnstyledAccountSettingsDisplayField = ({
  value, classes, onChange, errors, isProducer
}) => {
  const errorArray = Object.values(errors).filter(error => error !== undefined);
  return (
    <div className={ classes.container }>
      <div className={ classes.companyInfo }>Company Information</div>
      <div className={ classes.errors }>
        { errorArray.length > 0 && errorArray.map(error => <div style={ { color: 'red' } } key={ error }>{ error }</div>)}
      </div>
      <div className={ classes.group1 }>
        <TextField
          className="CCP__AccountSettingsForm__LegalName"
          onChange={ getOnChange(value, onChange, 'LegalName') }
          error={ errors.LegalName }
          value={ value.LegalName }
          underline={ classes.underline }
          label="Legal Name"
          style={ { width: '400px', marginRight: '100px' } }
        />
        <TextField
          onChange={ getOnChange(value, onChange, 'TradeName') }
          error={ errors.TradeName }
          value={ value.TradeName }
          underline={ classes.underline }
          style={ { width: '400px' } }
          label="Trade name (DBA) if different"
        />
      </div>
      <div className={ classes.group1 }>
        <AccountNumbersField
          onChange={ getOnChange(value, onChange, 'CarrierAccounts') }
          errors={ errors.CarrierAccounts }
          value={ value.CarrierAccounts }
          underline={ classes.underline }
        />
        <TextField
          disabled
          onChange={ getOnChange(value, onChange, 'FBPN') }
          error={ errors.FBPN }
          value={ value.FBPN }
          underline={ classes.underline }
          style={ { width: '400px' } }
          label={ isProducer ? 'Federal basic permit number' : 'License Number' }
        />
      </div>
      <div className={ classes.contactDetails }>Contact Details</div>
      <div className={ classes.group2 }>
        <TextField
          onChange={ getOnChange(value, onChange, 'Email') }
          error={ errors.Email }
          value={ value.Email }
          underline={ classes.underline }
          style={ { width: '400px', marginRight: '100px' } }
          label="Email"
        />
        <MaskedField
          onChange={ getOnChange(value, onChange, 'PhoneNumber') }
          error={ errors.PhoneNumber }
          value={ value.PhoneNumber }
          style={ { width: '400px' } }
          label="Phone Number"
          underline={ classes.underline }
          mask={ PhoneNumberMask }
        />
      </div>
      <div className={ classes.group2 }>
        <TextField
          onChange={ getOnChange(value, onChange, 'AddressLine') }
          error={ errors.AddressLine }
          value={ value.AddressLine }
          underline={ classes.underline }
          style={ { width: '400px', marginRight: '100px' } }
          label="Address"
        />
        <span className={ classes.cityStateZip }>
          <TextField
            onChange={ getOnChange(value, onChange, 'City') }
            error={ errors.City }
            value={ value.City }
            underline={ classes.underline }
            style={ { width: 250, marginRight: 20 } }
            label="City"
          />
          <MaskedField
            onChange={ getOnChange(value, onChange, 'State') }
            error={ errors.State }
            value={ value.State }
            underline={ classes.underline }
            style={ { width: 40, marginRight: 20 } }
            label="State"
            mask={ StateMask }
          />
          <MaskedField
            onChange={ getOnChange(value, onChange, 'ZipCode') }
            error={ errors.ZipCode }
            value={ value.ZipCode }
            underline={ classes.underline }
            style={ { width: 70 } }
            label="Zip Code"
            mask={ ZipCodeMask }
          />
        </span>
      </div>
    </div>
  );
};

UnstyledAccountSettingsDisplayField.propTypes = {
  value: PropTypes.object,
  classes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  isProducer: PropTypes.bool
};

UnstyledAccountSettingsDisplayField.defaultProps = {
  value: undefined,
  isProducer: true
};

TextField.propTypes = {
  value: PropTypes.string,
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  underline: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  style: PropTypes.object.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool
};

TextField.defaultProps = {
  value: '',
  error: undefined,
  className: undefined,
  disabled: false
};

MaskedField.propTypes = {
  value: PropTypes.string,
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  underline: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  style: PropTypes.object.isRequired,
  mask: PropTypes.func.isRequired
};

MaskedField.defaultProps = {
  value: '',
  error: undefined
};

export default withStyles(styles)(UnstyledAccountSettingsDisplayField);
